import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Eye, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useFlooScopeOptions,
  type FlooScopeOption,
  type FlooScopeType,
} from '@/hooks/useFlooScopeOptions';

// Floo Network Phase 6 — Scope Switcher UI.
//
// Compact dropdown that surfaces the active view scope and lets the
// user pick another scope from their allowed boundary. Renders nothing
// when only one scope is available (the shop-only / single-shop case)
// so single-shop Core users never see this control.
//
// Two variants — desktop (compact button) and mobile (in-sheet button
// list). Both consume the same hook so they stay in sync.

const LEVEL_ORDER: FlooScopeType[] = ['organization', 'region', 'district', 'shop'];

interface ScopeSwitcherProps {
  /**
   * 'desktop' renders a button dropdown sized to fit the TopBar row.
   * 'mobile' renders a vertical list suitable for the mobile sheet.
   */
  variant?: 'desktop' | 'mobile';
  /** Optional close-on-pick callback for the mobile sheet. */
  onPick?: () => void;
  className?: string;
}

export function ScopeSwitcher({ variant = 'desktop', onPick, className }: ScopeSwitcherProps) {
  const { options, activeScope, setActiveScope, canSwitchScope } = useFlooScopeOptions();

  // Hide entirely when there's nothing to switch. Phase 6 spec: shop-
  // only users should see no switcher when only one shop is available.
  if (!canSwitchScope || !activeScope) return null;

  // Group options by level for the dropdown. Inside each level, the
  // hook already returned items sorted alphabetically.
  const grouped: Partial<Record<FlooScopeType, FlooScopeOption[]>> = {};
  for (const o of options) {
    (grouped[o.type] ??= []).push(o);
  }

  if (variant === 'mobile') {
    return (
      <div className={cn('flex flex-col gap-1', className)} data-testid="floo-scope-switcher-mobile">
        <p className="text-xs font-semibold text-muted-foreground px-2 mb-1">
          Viewing — {activeScope.levelLabel} · {activeScope.label}
        </p>
        {LEVEL_ORDER.flatMap((level) => {
          const items = grouped[level] ?? [];
          if (items.length === 0) return [];
          return [
            <p key={`${level}-header`} className="text-[10px] uppercase tracking-wide text-muted-foreground/70 px-2 mt-1">
              {items[0].levelLabel}
            </p>,
            ...items.map((o) => (
              <Button
                key={`${o.type}:${o.id}`}
                variant={
                  activeScope.type === o.type && activeScope.id === o.id ? 'secondary' : 'ghost'
                }
                size="sm"
                onClick={() => {
                  setActiveScope(o);
                  onPick?.();
                }}
                className="justify-start gap-2 h-8"
                data-testid={`floo-scope-option-mobile-${o.type}-${o.id}`}
              >
                <Eye className="h-3 w-3" />
                {o.label}
              </Button>
            )),
          ];
        })}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-2', className)}
          data-testid="floo-scope-switcher-button"
          title="Switch the active OptiNetwork view"
        >
          <Eye className="h-4 w-4" />
          <span className="truncate max-w-[14ch]">
            <span className="text-muted-foreground mr-1">Viewing:</span>
            {activeScope.label}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Eye className="h-4 w-4" />
          Switch view
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LEVEL_ORDER.flatMap((level, idx) => {
          const items = grouped[level] ?? [];
          if (items.length === 0) return [];
          const prevLevelsHadItems = LEVEL_ORDER.slice(0, idx).some(
            (l) => (grouped[l] ?? []).length > 0,
          );
          return [
            ...(prevLevelsHadItems
              ? [<DropdownMenuSeparator key={`${level}-sep`} />]
              : []),
            <DropdownMenuLabel
              key={`${level}-header`}
              className="text-[10px] uppercase tracking-wide text-muted-foreground/70 py-1"
            >
              {items[0].levelLabel}
            </DropdownMenuLabel>,
            ...items.map((o) => (
              <DropdownMenuItem
                key={`${o.type}:${o.id}`}
                onClick={() => setActiveScope(o)}
                className={cn(
                  'gap-2',
                  activeScope.type === o.type && activeScope.id === o.id && 'bg-accent',
                )}
                data-testid={`floo-scope-option-${o.type}-${o.id}`}
              >
                <Eye className="h-3 w-3 opacity-50" />
                <span className="truncate">{o.label}</span>
              </DropdownMenuItem>
            )),
          ];
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
